/**
 * @description Returns a submit handler function that, when called, creates the personnel mapping and redirects the user
 *  back to the personnel list page
 */

import React from 'react';

import { ToastContext } from '@seccon/core/contexts/toast';
import decodeJson from '@seccon/shared/utils/decodeJson';

const useSubmit = (headerMap) => {
  // Keep track of the submission state
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Retrieve the toast API
  const toastApi = React.useContext(ToastContext);

  /**
   * Handles success by notifying the user and redirecting them back to the personnel page
   */
  async function handleSuccess () {
    // Wait for the message to be displayed before proceeding
    await toastApi.success('Personnel mapping definition was successfully created.');

    // Wait a few seconds before redirecting the user, so they can read the success message
    setTimeout(() => {
      const facilityId = (new URLSearchParams(window.location.search)).get('facilityId');

      window.location.href = `/personnel/list?facilityId=${ facilityId }`;
    }, 1000);
  }

  /**
   * Handles failure by notifying the user and re-enabling the form
   */
  function handleFailure ({ body: error }) {
    const msg = error?.message ? `Error: ${ error.message }` : 'New personnel mapping definition could not be created due to a server error. Try again later.';

    toastApi.error(msg);

    setIsSubmitting(false);
  }

  /**
   * Handles form submissions
   * @param data {name: string, syncOnSsn: string, fields: { [fieldKey: string]: [fieldValue: string] } } - form data object
   * @returns handles form submission by creating the new personnel mapping
   */
  function handleSubmit (data) {
    let hasSsnMapping = false;

    const requestBody = {
      mappingName: data.name.trim(),
      syncOnSsn: data.syncOnSsn,
      headerMapping: {}
    };

    // Iterate through the list of fields, match each field key to its header name and add the mapping pair to the request body
    for (const fieldIndex in data.fields) {
      const headerName = headerMap[fieldIndex];
      const fieldMap = data.fields[fieldIndex]?.value;

      // Skip empty fields
      if (fieldMap) {
        requestBody.headerMapping[headerName] = fieldMap;

        // Determine if this header was mapped to the ssn field (or if a field was already mapped)
        hasSsnMapping = hasSsnMapping || fieldMap.toLowerCase() === 'personnel.ssn';
      }
    }

    // Notify the user if the Social Security field has not been mapped to any column header and exit early
    if (!hasSsnMapping) {
      toastApi.error('Social Security Number field (ssn) must be mapped to a column header.');

      return false;
    }

    // Turn on submitting state
    setIsSubmitting(true);

    return fetch('/mapping', {
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      method: 'POST',
      body: JSON.stringify(requestBody)
    })
      .then(decodeJson)
      .then(handleSuccess)
      .catch(handleFailure);
  }

  return { handleSubmit, isSubmitting };
};

export default useSubmit;
